import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ArrowDown, ArrowLeft, Download, ImageUp, Loader2, Users } from 'lucide-react';
import { Avatar } from '@/shared/ui/Avatar';
import { IconButton } from '@/shared/ui/IconButton';
import { Dialog } from '@/shared/ui/Dialog';
import { dialogs } from '@/shared/ui/dialogStore';
import { toast } from '@/shared/ui/toastStore';
import { useAuthStore } from '@/entities/session/model/authStore';
import { useBreakpoint } from '@/shared/hooks/useBreakpoint';
import { useUserPhoto } from '@/features/presence/model/presenceHooks';
import { chatApi } from '../api/chatApi';
import { useChatStore } from '../model/chatStore';
import {
  parseKey,
  type ChatAttachment,
  type ChatConversation,
  type ChatMessage,
  type SharePayload,
} from '../model/types';
import { AuthImage, downloadAttachment } from './AuthImage';
import { Composer, type ComposerHandle } from './Composer';
import { MessageBubble } from './MessageBubble';
import { dayLabel } from './chatFormat';
import { GroupInfoDialog } from './GroupDialogs';

const EMPTY: ChatMessage[] = [];

export function ThreadView({ convKey, onBack }: { convKey: string; onBack?: () => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const myId = useAuthStore((s) => s.user?.userId ?? '');
  const thread = useChatStore((s) => s.threads[convKey]);
  const items = thread?.items ?? EMPTY;
  const conversation = useChatStore((s) => s.conversations.find((c) => c.key === convKey));
  const contact = useChatStore((s) => s.contacts.find((c) => `u:${c.userId}` === convKey));
  const typing = useChatStore((s) => s.typing[convKey]);
  const send = useChatStore((s) => s.send);
  const loadThread = useChatStore((s) => s.loadThread);
  const setOpen = useChatStore((s) => s.setOpen);
  const target = parseKey(convKey);
  const isGroup = !!target.groupId;
  const title = conversation?.title ?? contact?.userName ?? '';
  const photo = useUserPhoto(target.peerUserId ?? '');

  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [editing, setEditing] = useState<ChatMessage | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [image, setImage] = useState<{ url: string; name: string } | null>(null);
  const [groupInfo, setGroupInfo] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const scroller = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const composer = useRef<ComposerHandle>(null);
  // Kullanıcı en altta mı: yeni mesaj / büyüyen içerik yalnız bu durumda aşağı kaydırır.
  const stick = useRef(true);
  const prevHeight = useRef<number | null>(null);
  const initialDone = useRef(false);
  const lastId = useRef<string | null>(null);
  const isMine = (m: ChatMessage) => m.senderId.toLowerCase() === myId.toLowerCase();

  // Açılıştaki okunmamış sayısı (bileşen konuşma başına yeniden kurulur): "okunmamış mesajlar"
  // ayırıcısı ve ilk kaydırma konumu buna göre. Okundu işaretlenince sayaç sıfırlansa da ayırıcı
  // konuşma kapanana dek yerinde kalır.
  const [unreadAtOpen] = useState(() => conversation?.unread ?? 0);
  const firstUnreadId = useMemo(() => {
    if (!thread?.loaded || unreadAtOpen <= 0) return null;
    let n = unreadAtOpen;
    let id: string | null = null;
    for (let i = items.length - 1; i >= 0 && n > 0; i--) {
      const m = items[i]!;
      if (m.senderId.toLowerCase() !== myId.toLowerCase()) {
        id = m.messageId;
        n--;
      }
    }
    return id;
    // Yalnız ilk yüklemede hesaplanır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.loaded]);

  const toBottom = (smooth = false) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    stick.current = true;
    setNewCount(0);
  };

  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    // Eski sayfa yüklendi: içerik yukarıdan eklendi, görünen yer korunur.
    if (prevHeight.current != null) {
      if (thread?.loading) return;
      el.scrollTop = el.scrollHeight - prevHeight.current;
      prevHeight.current = null;
      return;
    }
    const last = items[items.length - 1];
    if (!initialDone.current) {
      if (!thread?.loaded) return;
      initialDone.current = true;
      lastId.current = last?.messageId ?? null;
      const divider = el.querySelector<HTMLElement>('[data-unread-divider]');
      if (divider && divider.offsetTop > el.clientHeight / 2) {
        el.scrollTop = divider.offsetTop - 12;
        stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      } else toBottom();
      return;
    }
    if (!last) return;
    // Geçici kaydın gerçeğiyle değişmesi "yeni mesaj" sayılmaz.
    const changed =
      last.messageId !== lastId.current &&
      !(isMine(last) && !last.pending && lastId.current?.startsWith('tmp-'));
    lastId.current = last.messageId;
    if (!changed) return;
    if (stick.current || isMine(last)) toBottom();
    else setNewCount((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, thread?.loaded, thread?.loading]);

  // İçerik sonradan büyürse (resim yüklendi, yazan göstergesi, yazma kutusu uzadı) en altta kalınır.
  useEffect(() => {
    const el = scroller.current;
    const inner = content.current;
    if (!el || !inner || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (stick.current && prevHeight.current == null) el.scrollTop = el.scrollHeight;
    });
    ro.observe(inner);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Sekmeye dönünce (arka planda gelen mesajlar) konuşma okundu sayılır.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const c = useChatStore.getState().conversations.find((x) => x.key === convKey);
      if (c && c.unread > 0) useChatStore.getState().markRead(convKey);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [convKey]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (stick.current && newCount) setNewCount(0);
    if (el.scrollTop < 60 && thread?.hasMore && !thread.loading && prevHeight.current == null) {
      prevHeight.current = el.scrollHeight;
      void loadThread(convKey, true);
    }
  };

  const jumpTo = (messageId: string) => {
    const el = scroller.current?.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`);
    if (!el) {
      toast.info(t('chat.olderMessage'));
      return;
    }
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.animate([{ backgroundColor: 'rgb(250 204 21 / 0.35)' }, { backgroundColor: 'transparent' }], {
      duration: 1600,
    });
  };

  const onSend = async (content: string, attachments: ChatAttachment[]) => {
    const saved = await send(convKey, {
      content,
      attachmentIds: attachments.map((a) => a.attachmentId),
      attachments,
      replyTo,
    });
    if (saved) setReplyTo(null);
    else toast.error(t('chat.sendFailed'));
    return !!saved;
  };

  const openShare = (p: SharePayload) => {
    navigate(p.url);
    if (isMobile) setOpen(false);
  };

  const typers = (typing ?? []).filter((x) => x.until > Date.now());
  const status = typers.length
    ? isGroup
      ? t('chat.typingNamed', { name: typers.map((x) => x.name).join(', ') })
      : t('chat.typing')
    : isGroup
      ? t('chat.members', { count: conversation?.memberCount ?? 0 })
      : contact?.isOnline
        ? t('chat.online')
        : '';

  const rows = useMemo(() => {
    let lastDay = '';
    return items.map((m, i) => {
      const day = new Date(m.sentAt).toDateString();
      const showDay = day !== lastDay;
      lastDay = day;
      const prev = items[i - 1];
      const showSender = isGroup && (showDay || !prev || prev.senderId !== m.senderId);
      return { m, showDay, showSender };
    });
  }, [items, isGroup]);

  return (
    <div
      className="relative flex min-h-0 flex-1 flex-col"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          setDragOver(true);
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        composer.current?.addFiles(Array.from(e.dataTransfer.files));
      }}
    >
      <div className="border-border flex h-14 shrink-0 items-center gap-2 border-b px-2">
        {onBack ? (
          <IconButton label={t('chat.back')} onClick={onBack} size="sm">
            <ArrowLeft className="h-4 w-4" />
          </IconButton>
        ) : null}
        {isGroup ? (
          <span className="bg-brand-500/15 text-brand-600 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
            <Users className="h-4 w-4" />
          </span>
        ) : (
          <span className="relative">
            <Avatar name={title || '?'} imageBase64={photo} className="h-9 w-9" />
            {contact?.isOnline ? (
              <span className="ring-bg-elevated absolute right-0 bottom-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2" />
            ) : null}
          </span>
        )}
        <button
          type="button"
          disabled={!isGroup}
          onClick={() => setGroupInfo(true)}
          className="min-w-0 flex-1 text-left disabled:cursor-default"
        >
          <span className="block truncate text-sm font-semibold">{title}</span>
          <span
            className={
              typers.length ? 'text-brand-600 block truncate text-xs' : 'text-fg-muted block truncate text-xs'
            }
          >
            {status}
          </span>
        </button>
        {isGroup ? (
          <IconButton label={t('chat.groupInfo')} size="sm" onClick={() => setGroupInfo(true)}>
            <Users className="h-4 w-4" />
          </IconButton>
        ) : null}
      </div>

      <div
        ref={scroller}
        onScroll={onScroll}
        className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain"
      >
        <div ref={content} className="flex min-h-full flex-col gap-1 py-3">
          {thread?.loading && thread.items.length > 0 ? (
            <div className="flex justify-center py-1">
              <Loader2 className="text-fg-subtle h-4 w-4 animate-spin" />
            </div>
          ) : null}
          {!thread?.loaded ? (
            <div className="flex flex-1 items-center justify-center">
              <Loader2 className="text-fg-subtle h-5 w-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-fg-subtle flex flex-1 items-center justify-center px-6 text-center text-sm">
              {t('chat.emptyThread')}
            </div>
          ) : null}
          {rows.map(({ m, showDay, showSender }) => (
            <Fragment key={m.messageId}>
              {m.messageId === firstUnreadId ? (
                <div data-unread-divider className="my-2 flex items-center gap-2 px-4">
                  <span className="bg-brand-500/40 h-px flex-1" />
                  <span className="text-brand-600 text-[11px] font-semibold">
                    {t('chat.unreadDivider', { count: unreadAtOpen })}
                  </span>
                  <span className="bg-brand-500/40 h-px flex-1" />
                </div>
              ) : null}
              {showDay ? (
                <div className="my-2 flex justify-center">
                  <span className="bg-bg-muted text-fg-muted rounded-full px-3 py-0.5 text-[11px]">
                    {dayLabel(m.sentAt, i18n.language, t('chat.today'), t('chat.yesterday'))}
                  </span>
                </div>
              ) : null}
              <MessageBubble
                message={m}
                mine={m.senderId.toLowerCase() === myId.toLowerCase()}
                showSender={showSender}
                isGroup={isGroup}
                myUserId={myId}
                onReply={setReplyTo}
                onEdit={setEditing}
                onDelete={async (msg) => {
                  if (await dialogs.confirm({ kind: 'delete', message: t('chat.deleteConfirm') }))
                    void chatApi.remove(msg.messageId);
                }}
                onReact={(msg, emoji) => void chatApi.react(msg.messageId, emoji)}
                onRetry={(msg) => {
                  useChatStore.setState((s) => ({
                    threads: {
                      ...s.threads,
                      [convKey]: {
                        ...s.threads[convKey]!,
                        items: s.threads[convKey]!.items.filter((x) => x.messageId !== msg.messageId),
                      },
                    },
                  }));
                  void send(convKey, {
                    content: msg.content,
                    attachments: msg.attachments,
                    attachmentIds: msg.attachments.map((a) => a.attachmentId),
                  });
                }}
                onOpenImage={(url, name) => setImage({ url, name })}
                onOpenShare={openShare}
                onJumpTo={jumpTo}
              />
            </Fragment>
          ))}
          {typers.length ? (
            <div className="px-4 pt-1">
              <span className="bg-bg-muted inline-flex gap-1 rounded-2xl px-3 py-2">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="bg-fg-subtle h-1.5 w-1.5 animate-bounce rounded-full"
                    style={{ animationDelay: `${i * 150}ms` }}
                  />
                ))}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {newCount > 0 ? (
        <button
          type="button"
          onClick={() => toBottom(true)}
          className="bg-bg-elevated border-border text-brand-600 hover:bg-bg-hover absolute right-4 bottom-20 z-10 flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold shadow-[var(--shadow)]"
        >
          <ArrowDown className="h-3.5 w-3.5" />
          {t('chat.newMessages', { count: newCount })}
        </button>
      ) : null}

      <Composer
        ref={composer}
        replyTo={replyTo}
        editing={editing}
        onCancelReply={() => setReplyTo(null)}
        onCancelEdit={() => setEditing(null)}
        onSend={onSend}
        onSaveEdit={async (m, content) => {
          try {
            await chatApi.edit(m.messageId, content);
            setEditing(null);
            return true;
          } catch {
            toast.error(t('chat.sendFailed'));
            return false;
          }
        }}
        onTyping={() => void chatApi.typing(target)}
      />

      {dragOver ? (
        <div className="border-brand-500 bg-brand-500/10 text-brand-700 dark:text-brand-300 pointer-events-none absolute inset-2 z-10 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed text-sm font-medium">
          <ImageUp className="h-8 w-8" />
          {t('chat.dropHere')}
        </div>
      ) : null}

      {image ? (
        <Dialog
          open
          onOpenChange={(o) => !o && setImage(null)}
          title={image.name}
          size="xl"
          titleExtra={
            <IconButton
              label={t('chat.download')}
              size="sm"
              onClick={() => void downloadAttachment(image.url, image.name)}
            >
              <Download className="h-4 w-4" />
            </IconButton>
          }
        >
          <div className="flex justify-center">
            <AuthImage
              url={image.url}
              alt={image.name}
              className="max-h-[75vh] w-auto max-w-full object-contain"
            />
          </div>
        </Dialog>
      ) : null}
      {groupInfo && target.groupId ? (
        <GroupInfoDialog
          groupId={target.groupId}
          onClose={() => setGroupInfo(false)}
          conversation={conversation as ChatConversation | undefined}
        />
      ) : null}
    </div>
  );
}
